import { Fragment, useRef, useState } from 'react';

import { Icon, type IconName } from '../components/Icon.js';
import { Menu, MenuItem } from '../components/Menu.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './ChatRail.module.css';

export type ChatSection =
  'chat' | 'lists' | 'event' | 'forms' | 'autoReply' | 'contacts' | 'ended' | 'broadcast';

interface RailItem {
  section: ChatSection;
  icon: IconName;
  label: string;
  /** One of the phone pill's own targets; everything else folds under More
   *  (see `overflow` in the component). Also what the stylesheet reads, as
   *  `data-pill`. */
  pill?: true;
}

/** Three views onto the same pool of conversations. */
const CONVERSATIONS: RailItem[] = [
  { section: 'chat', icon: 'chatBubble', label: 'Chats', pill: true },
  { section: 'lists', icon: 'bookmark', label: 'Saved lists', pill: true },
  { section: 'ended', icon: 'chatOff', label: 'Ended conversations' },
];

/**
 * The employer's own material: authored once, kept, and reused, and delivering
 * messages into chats from outside them. That is what earns each a destination
 * rather than a control tucked into a composer, and what the one divider marks.
 */
const AUTHORED: RailItem[] = [
  { section: 'event', icon: 'calendar', label: 'Events', pill: true },
  { section: 'forms', icon: 'listChecks', label: 'Forms' },
  // The bolt is the mark an automatic reply already wears in the thread.
  { section: 'autoReply', icon: 'bolt', label: 'Auto reply' },
  { section: 'contacts', icon: 'users', label: 'Contacts' },
];

/**
 * Platform news, which is neither a conversation nor the employer's own work,
 * so it rests on the floor of the column under space rather than a divider: a
 * rule fencing one item separates it from nothing.
 */
const PLATFORM: RailItem[] = [{ section: 'broadcast', icon: 'megaphone', label: 'Info' }];

/** The rail's two bands, top to bottom; `PLATFORM` rests below them. */
const BANDS: RailItem[][] = [CONVERSATIONS, AUTHORED];

/** How many of the hidden sections the More face shows at thumbnail size. */
const FACE_ICONS = 3;

interface ChatRailProps {
  section: ChatSection;
  onSection: (section: ChatSection) => void;
  onStartChat?: () => void;
  /** Which destinations have something waiting. A lookup rather than a flag per
   *  section, so the rail stays the data-driven nav it already is. */
  dots: Partial<Record<ChatSection, boolean>>;
  /** Which destinations this account cannot use. Same lookup shape as `dots`,
   *  for the same reason. A gated section is ABSENT — not greyed, not carrying a
   *  tooltip: a dead control with no answer behind it only earns a support
   *  ticket. Hiding it is presentation; the BFF is what actually refuses. */
  hidden: Partial<Record<ChatSection, boolean>>;
}

export function ChatRail({ section, onSection, onStartChat, dots, hidden }: ChatRailProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLSpanElement>(null);
  useDismiss(moreOpen, () => setMoreOpen(false), moreRef);

  const renderItem = (item: RailItem) => (
    <button
      key={item.section}
      className={section === item.section ? styles.itemActive : styles.item}
      // Lets the mobile pill target sections by name — reorder them, fold
      // some under More and raise new-chat into a centre FAB — without hashing
      // (plain attribute).
      data-section={item.section}
      data-pill={item.pill ? '' : undefined}
      title={item.label}
      aria-label={item.label}
      onClick={() => onSection(item.section)}
    >
      <Icon name={item.icon} size={22} />
      {dots[item.section] && <span className={styles.dot} />}
    </button>
  );

  const shown = (item: RailItem) => !hidden[item.section];

  // A band every one of whose sections is gated away goes with its divider, so
  // hiding one never leaves a stray rule or two rules stacked together.
  const bands = BANDS.map((band) => band.filter(shown)).filter((band) => band.length > 0);
  const floor = PLATFORM.filter(shown);

  /**
   * What the phone's pill holds behind "More".
   *
   * The pill is five targets and no more: two either side of the centre FAB.
   * Seven or more destinations at 44px apiece do not fit a phone, and a nav that
   * scrolls sideways is a nav with sections nobody finds. So the pill keeps
   * the two places things arrive at (Chats, Events) and the one the employer
   * sorts them into (Saved lists), marked `pill` on the item, and the rest sit
   * one press deeper: the archive, the authoring sections and the
   * platform's news. Same destinations, same names, one of them a menu.
   * Desktop has the room and shows all of them.
   *
   * Derived, so the pill, the menu and the stylesheet (which hides whatever
   * has no `data-pill`) cannot disagree about which sections those are. Order
   * here is menu order, which mirrors the rail's own top to bottom.
   */
  const overflow = [...bands.flat(), ...floor].filter((item) => !item.pill);
  const inMore = overflow.some((item) => item.section === section);
  const moreDot = overflow.some((item) => dots[item.section]);

  return (
    <nav className={styles.rail}>
      {bands.map((band, index) => (
        <Fragment key={band[0]!.section}>
          {index > 0 && <div className={styles.divider} />}
          <div className={styles.group}>{band.map(renderItem)}</div>
        </Fragment>
      ))}
      {/* Held to the bottom by `.floor`'s auto margin, which is the whole of its
          separation from the band above (see `PLATFORM`). On mobile the pill
          dissolves its container, so the margin has nothing to push there. */}
      {floor.length > 0 && <div className={styles.floor}>{floor.map(renderItem)}</div>}
      {/* Start-chat is an action, not a destination: it asks the host to open its
          own popup, so it takes no `section` and has no active state. Hidden on
          the desktop rail (the inbox header's "+" owns it there) and shown as the
          centre FAB on mobile. `.fab` owns its own mobile order — see the CSS. */}
      {onStartChat && (
        <button
          type="button"
          className={styles.fab}
          title="New chat"
          aria-label="New chat"
          onClick={onStartChat}
        >
          <Icon name="messageCirclePlus" size={22} />
        </button>
      )}
      {/* The pill's fifth target, mobile only (see `overflow`). Lit while the
          open section is one of the ones it holds, so the pill always shows
          where you are even when where you are is behind it. A hidden section's
          dot climbs up onto it for the same reason: a menu must not be able to
          bury the one red mark.

          Its face is what it holds: the first three hidden sections at
          thumbnail size and a count for the rest, in a two-by-two, the way a
          phone draws a folder of apps. A generic grid glyph said "more
          things"; this says which things, so an employer looking for Forms
          sees its mark before they press anything. */}
      <span className={styles.moreWrap} ref={moreRef}>
        <button
          type="button"
          className={inMore ? styles.moreActive : styles.more}
          title="More"
          aria-label="More sections"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((current) => !current)}
        >
          <span className={styles.moreGrid} aria-hidden="true">
            {overflow.slice(0, FACE_ICONS).map((item) => (
              <span key={item.section} className={styles.moreCell}>
                <Icon name={item.icon} size={12} />
              </span>
            ))}
            {overflow.length > FACE_ICONS && (
              <span className={`${styles.moreCell} ${styles.moreCount}`}>
                +{overflow.length - FACE_ICONS}
              </span>
            )}
          </span>
          {moreDot && <span className={styles.dot} />}
        </button>
        {moreOpen && (
          <Menu placement="up" onClose={() => setMoreOpen(false)}>
            {overflow.map((item) => (
              <MenuItem
                key={item.section}
                icon={item.icon}
                onSelect={() => onSection(item.section)}
              >
                {item.label}
                {dots[item.section] && (
                  <span className={styles.menuDot} role="img" aria-label="Unread" />
                )}
              </MenuItem>
            ))}
          </Menu>
        )}
      </span>
    </nav>
  );
}
